import { useRef, useEffect, useState } from "react";
import { useScroll, motion } from "framer-motion";

// ─── Math helpers ────────────────────────────────────────────────────────────
const DEG = Math.PI / 180;
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

function lerpAngle(a: number, b: number, t: number) {
  let d = b - a;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return a + d * t;
}

function project(lon: number, lat: number, cLon: number, cLat: number, r: number) {
  const λ = lon * DEG, φ = lat * DEG, λ0 = cLon * DEG, φ0 = cLat * DEG;
  const c =
    Math.sin(φ0) * Math.sin(φ) +
    Math.cos(φ0) * Math.cos(φ) * Math.cos(λ - λ0);
  if (c < 0) return null;
  return {
    x: r * Math.cos(φ) * Math.sin(λ - λ0),
    y: r * (Math.cos(φ0) * Math.sin(φ) - Math.sin(φ0) * Math.cos(φ) * Math.cos(λ - λ0)),
    c,
  };
}

function greatCirclePts(
  lon1: number, lat1: number,
  lon2: number, lat2: number,
  n = 120
): [number, number][] {
  const φ1 = lat1 * DEG, λ1 = lon1 * DEG;
  const φ2 = lat2 * DEG, λ2 = lon2 * DEG;
  const d = 2 * Math.asin(
    Math.sqrt(
      Math.sin((φ2 - φ1) / 2) ** 2 +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin((λ2 - λ1) / 2) ** 2
    )
  );
  if (d < 1e-6) return [[lon1, lat1]];
  return Array.from({ length: n + 1 }, (_, i) => {
    const f = i / n;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
    const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
    const z = A * Math.sin(φ1) + B * Math.sin(φ2);
    return [
      Math.atan2(y, x) / DEG,
      Math.atan2(z, Math.sqrt(x * x + y * y)) / DEG,
    ] as [number, number];
  });
}

// ─── Data ────────────────────────────────────────────────────────────────────
const CITIES = {
  dubai:        { lon: 55.27,   lat: 25.20,  label: "Dubai" },
  singapore:    { lon: 103.82,  lat: 1.35,   label: "Singapore" },
  sydney:       { lon: 151.21,  lat: -33.87, label: "Sydney" },
  toronto:      { lon: -79.38,  lat: 43.65,  label: "Toronto" },
  mumbai:       { lon: 72.88,   lat: 19.08,  label: "Mumbai" },
  manila:       { lon: 120.98,  lat: 14.60,  label: "Manila" },
  dhaka:        { lon: 90.41,   lat: 23.81,  label: "Dhaka" },
  taipei:       { lon: 121.56,  lat: 25.04,  label: "Taipei" },
  hanoi:        { lon: 105.85,  lat: 21.03,  label: "Hanoi" },
  newyork:      { lon: -74.01,  lat: 40.71,  label: "New York" },
  london:       { lon: -0.13,   lat: 51.51,  label: "London" },
  frankfurt:    { lon: 8.68,    lat: 50.11,  label: "Frankfurt" },
  seoul:        { lon: 126.98,  lat: 37.57,  label: "Seoul" },
  jakarta:      { lon: 106.85,  lat: -6.21,  label: "Jakarta" },
  nairobi:      { lon: 36.82,   lat: -1.29,  label: "Nairobi" },
  saopaulo:     { lon: -46.63,  lat: -23.55, label: "São Paulo" },
  lagos:        { lon: 3.39,    lat: 6.45,   label: "Lagos" },
  johannesburg: { lon: 28.04,   lat: -26.20, label: "Johannesburg" },
  tokyo:        { lon: 139.69,  lat: 35.69,  label: "Tokyo" },
  shanghai:     { lon: 121.47,  lat: 31.23,  label: "Shanghai" },
  moscow:       { lon: 37.62,   lat: 55.76,  label: "Moscow" },
  buenosaires:  { lon: -58.38,  lat: -34.61, label: "Buenos Aires" },
  istanbul:     { lon: 28.98,   lat: 41.01,  label: "Istanbul" },
  karachi:      { lon: 67.01,   lat: 24.86,  label: "Karachi" },
} as const;

type CityKey = keyof typeof CITIES;
const CITY_KEYS = Object.keys(CITIES) as CityKey[];

interface Chapter {
  from: CityKey | null;
  to: CityKey | null;
  cLon: number;
  cLat: number;
  num: string;
  route: string | null;
  label: string;
  title: string;
  body: string;
  color: string;
  active: CityKey[];
}

const CHAPTERS: Chapter[] = [
  {
    from: "sydney", to: null,
    cLon: 151, cLat: -28,
    num: "00", route: null,
    label: "Origin",
    title: "Built in Australia. Built for the world.",
    body: "TICCA is an Australian company. The team is distributed by design — operating across timezones, not because we plan to expand later, but because distributed work is how we build.",
    color: "#074EDC",
    active: ["sydney"],
  },
  {
    from: "sydney", to: "singapore",
    cLon: 128, cLat: -10,
    num: "01", route: null,
    label: "Distributed",
    title: "A team across timezones.",
    body: "TICCA operates across multiple timezones with team members contributing from regions around the world. Asynchronous communication, structured collaboration, and clear documentation are how we work every day.",
    color: "#4AAC00",
    active: ["sydney", "singapore", "toronto", "london", "dubai"],
  },
  {
    from: "sydney", to: "dubai",
    cLon: 105, cLat: 5,
    num: "02", route: null,
    label: "Sovereign",
    title: "Data sovereignty by design.",
    body: "For organisations adopting AI, data sovereignty is not optional. TICCA's operating model is built around the principle that data remains under the control of the organisation that owns it, governed by the rules that apply to it.",
    color: "#FF8C00",
    active: ["sydney", "dubai", "singapore", "london", "toronto"],
  },
  {
    from: "sydney", to: "london",
    cLon: 30, cLat: 15,
    num: "03", route: null,
    label: "AI Operations",
    title: "Built for the way AI will be used.",
    body: "AI does not work 9-to-5 in one location. It runs across systems, timezones, and jurisdictions. The operating layer around AI work must be as distributed as the AI itself.",
    color: "#ED0216",
    active: ["sydney", "london", "singapore", "toronto", "dubai"],
  },
  {
    from: "sydney", to: "newyork",
    cLon: -120, cLat: -5,
    num: "04", route: null,
    label: "Capability",
    title: "Around the clock. Around the world.",
    body: "Distributed operation gives TICCA a unique advantage: work does not stop when one timezone ends its day. Asynchronous coordination means progress continues across cycles, with evidence and accountability preserved at every handoff.",
    color: "#4AAC00",
    active: ["sydney", "toronto", "newyork", "london", "singapore"],
  },
  {
    from: null, to: null,
    cLon: 25, cLat: -15,
    num: "05", route: null,
    label: "Mission",
    title: "One team. One purpose.",
    body: "Every person at TICCA is focused on one thing: building accountable AI work systems. We are early, we are building, and we are honest about both. We would rather show honest progress than overstate our stage.",
    color: "#074EDC",
    active: ["sydney", "singapore", "toronto", "london", "dubai"],
  },
];

const ARC_DEFS: { from: CityKey; to: CityKey; color: string }[] = [
  // Sydney as primary hub — radiating outward in all directions
  { from: "sydney",       to: "singapore",    color: "#4AAC00" },
  { from: "sydney",       to: "tokyo",        color: "#4AAC00" },
  { from: "sydney",       to: "manila",       color: "#4AAC00" },
  { from: "sydney",       to: "jakarta",      color: "#4AAC00" },
  { from: "sydney",       to: "dubai",        color: "#FF8C00" },
  { from: "sydney",       to: "mumbai",       color: "#FF8C00" },
  { from: "sydney",       to: "london",       color: "#ED0216" },
  { from: "sydney",       to: "johannesburg", color: "#ED0216" },
  { from: "sydney",       to: "newyork",      color: "#074EDC" },
  { from: "sydney",       to: "toronto",      color: "#074EDC" },
  { from: "sydney",       to: "saopaulo",     color: "#074EDC" },
  // Secondary connections from other hubs
  { from: "singapore",    to: "seoul",        color: "#4AAC00" },
  { from: "singapore",    to: "shanghai",     color: "#4AAC00" },
  { from: "singapore",    to: "dhaka",        color: "#4AAC00" },
  { from: "singapore",    to: "hanoi",        color: "#4AAC00" },
  { from: "dubai",        to: "istanbul",     color: "#FF8C00" },
  { from: "dubai",        to: "karachi",      color: "#FF8C00" },
  { from: "dubai",        to: "nairobi",      color: "#FF8C00" },
  { from: "london",       to: "frankfurt",    color: "#ED0216" },
  { from: "london",       to: "moscow",       color: "#ED0216" },
  { from: "london",       to: "lagos",        color: "#ED0216" },
  { from: "london",       to: "newyork",      color: "#074EDC" },
  { from: "frankfurt",    to: "moscow",       color: "#ED0216" },
  { from: "nairobi",      to: "johannesburg", color: "#ED0216" },
  { from: "newyork",      to: "saopaulo",     color: "#074EDC" },
  { from: "saopaulo",     to: "buenosaires",  color: "#074EDC" },
  { from: "toronto",      to: "newyork",      color: "#074EDC" },
  { from: "seoul",        to: "tokyo",        color: "#4AAC00" },
  { from: "tokyo",        to: "taipei",       color: "#4AAC00" },
  { from: "mumbai",       to: "karachi",      color: "#FF8C00" },
  { from: "mumbai",       to: "dhaka",        color: "#FF8C00" },
];

const ARC_PTS = ARC_DEFS.map(({ from, to }) =>
  greatCirclePts(CITIES[from].lon, CITIES[from].lat, CITIES[to].lon, CITIES[to].lat)
);

// Which chapter each arc becomes active in (aligns with CHAPTERS above)
// Spread arcs evenly across 6 chapters (0-5)
const ARC_CHAPTER = ARC_DEFS.map((_, i) => {
  if (i <= 2) return 0;
  if (i <= 6) return 1;
  if (i <= 10) return 2;
  if (i <= 14) return 3;
  if (i <= 19) return 4;
  return 5;
});

// Stable quasi-random stars via golden angle
const STARS = Array.from({ length: 220 }, (_, i) => {
  const θ = i * 2.3999632;
  const ρ = 0.26 + (i % 11) * 0.044;
  return {
    x: 0.5 + Math.cos(θ) * ρ,
    y: 0.5 + Math.sin(θ) * ρ,
    s: 0.28 + (i % 9) * 0.11,
    a: 0.07 + (i % 8) * 0.055,
    speed: 0.6 + (i % 7) * 0.18,
  };
});

// ─── Component ───────────────────────────────────────────────────────────────
export function GlobeScrollytelling() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const rafRef       = useRef<number>(0);
  const scrollRef    = useRef({ ch: 0 });
  const animRef      = useRef({
    cLon: 0,
    cLat: 20,
    arcProg: Array(ARC_DEFS.length).fill(0) as number[],
    t: 0,
  });

  const [chapter, setChapter] = useState(0);

  // Scroll progress measured from container-top-at-viewport-top → container-bottom-at-viewport-top
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  useEffect(
    () =>
      scrollYProgress.on("change", (v) => {
        // 500vh container, 6 chapters
        const raw = Math.min(v * 7, CHAPTERS.length - 0.001);
        const ch = Math.floor(raw);
        scrollRef.current.ch = ch;
        setChapter(ch);
      }),
    [scrollYProgress]
  );

  // DPI-aware canvas resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const sync = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width  = canvas.clientWidth  * dpr;
      canvas.height = canvas.clientHeight * dpr;
      const ctx = canvas.getContext("2d");
      ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  // RAF draw loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const draw = (ts: number) => {
      const ctx = canvas.getContext("2d");
      if (!ctx) { rafRef.current = requestAnimationFrame(draw); return; }

      const W = canvas.clientWidth;
      const H = canvas.clientHeight;
      const a  = animRef.current;
      const ch = scrollRef.current.ch;
      const chData = CHAPTERS[Math.min(ch, CHAPTERS.length - 1)];

      a.t = ts * 0.001;

      a.cLon = reduced ? chData.cLon : lerpAngle(a.cLon, chData.cLon, 0.026);
      a.cLat = reduced ? chData.cLat : lerp(a.cLat, chData.cLat, 0.026);

      for (let i = 0; i < ARC_DEFS.length; i++) {
        const arcCh = ARC_CHAPTER[i];
        if (reduced || ch > arcCh) {
          a.arcProg[i] = 1;
        } else if (ch === arcCh) {
          a.arcProg[i] = Math.min(a.arcProg[i] + 0.007, 1);
        }
      }

      ctx.clearRect(0, 0, W, H);

      // Globe position — right half on landscape, top-center on portrait
      const portrait = H > W * 1.05;
      const cx = portrait ? W * 0.5  : W * 0.65;
      const cy = portrait ? H * 0.38 : H * 0.5;
      const r  = Math.min(400, portrait
        ? Math.min(W * 0.40, H * 0.26)
        : Math.min(W * 0.34, H * 0.44));

      // Stars (outside globe)
      for (const s of STARS) {
        const sx = cx + (s.x - 0.5) * W * (portrait ? 2.0 : 1.5);
        const sy = cy + (s.y - 0.5) * H * (portrait ? 1.2 : 1.7);
        const dx = sx - cx, dy = sy - cy;
        if (dx * dx + dy * dy < r * r * 1.15) continue;
        const tw = reduced ? s.a : s.a * (0.65 + 0.35 * Math.sin(a.t * s.speed + sx * 0.01));
        ctx.beginPath();
        ctx.arc(sx, sy, s.s, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(190,210,255,${tw})`;
        ctx.fill();
      }

      // Atmosphere
      const atmo = ctx.createRadialGradient(cx, cy, r * 0.86, cx, cy, r * 1.38);
      atmo.addColorStop(0,   "rgba(7,78,220,0.30)");
      atmo.addColorStop(0.5, "rgba(7,78,220,0.07)");
      atmo.addColorStop(1,   "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(cx, cy, r * 1.38, 0, Math.PI * 2);
      ctx.fillStyle = atmo;
      ctx.fill();

      // Ocean
      const ocean = ctx.createRadialGradient(cx - r * 0.27, cy - r * 0.22, 0, cx, cy, r);
      ocean.addColorStop(0,    "#1c3b6a");
      ocean.addColorStop(0.52, "#0c2040");
      ocean.addColorStop(1,    "#060f1c");
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = ocean;
      ctx.fill();

      // Globe clip
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.clip();

      // Graticule
      ctx.lineWidth = 0.5;
      ctx.strokeStyle = "rgba(100,150,255,0.13)";
      for (let lat = -60; lat <= 60; lat += 30) {
        ctx.beginPath();
        let fst = true;
        for (let lon = -180; lon <= 180; lon += 3) {
          const p = project(lon, lat, a.cLon, a.cLat, r);
          if (!p) { fst = true; continue; }
          fst ? ctx.moveTo(cx + p.x, cy - p.y) : ctx.lineTo(cx + p.x, cy - p.y);
          fst = false;
        }
        ctx.stroke();
      }
      for (let lon = -180; lon < 180; lon += 30) {
        ctx.beginPath();
        let fst = true;
        for (let lat = -90; lat <= 90; lat += 3) {
          const p = project(lon, lat, a.cLon, a.cLat, r);
          if (!p) { fst = true; continue; }
          fst ? ctx.moveTo(cx + p.x, cy - p.y) : ctx.lineTo(cx + p.x, cy - p.y);
          fst = false;
        }
        ctx.stroke();
      }
      // Equator emphasis
      ctx.lineWidth = 0.9;
      ctx.strokeStyle = "rgba(120,170,255,0.22)";
      ctx.beginPath();
      let feq = true;
      for (let lon = -180; lon <= 180; lon += 2) {
        const p = project(lon, 0, a.cLon, a.cLat, r);
        if (!p) { feq = true; continue; }
        feq ? ctx.moveTo(cx + p.x, cy - p.y) : ctx.lineTo(cx + p.x, cy - p.y);
        feq = false;
      }
      ctx.stroke();

      // Arcs
      for (let i = 0; i < ARC_DEFS.length; i++) {
        if (a.arcProg[i] <= 0) continue;
        const pts      = ARC_PTS[i];
        const col      = ARC_DEFS[i].color;
        const isActive = ch === ARC_CHAPTER[i];
        const endIdx   = Math.floor(pts.length * a.arcProg[i]);

        ctx.beginPath();
        let farc = true;
        for (let j = 0; j <= endIdx && j < pts.length; j++) {
          const p = project(pts[j][0], pts[j][1], a.cLon, a.cLat, r);
          if (!p) { farc = true; continue; }
          farc ? ctx.moveTo(cx + p.x, cy - p.y) : ctx.lineTo(cx + p.x, cy - p.y);
          farc = false;
        }
        ctx.strokeStyle = col + (isActive ? "ff" : "99");
        ctx.lineWidth   = isActive ? 2.5 : 1.5;
        if (!reduced) {
          ctx.shadowColor = col;
          ctx.shadowBlur  = isActive ? 18 : 7;
        }
        ctx.stroke();
        ctx.shadowBlur = 0;

        if (isActive && a.arcProg[i] < 1 && !reduced) {
          const tip = pts[Math.min(endIdx, pts.length - 1)];
          const p   = project(tip[0], tip[1], a.cLon, a.cLat, r);
          if (p) {
            const pulse = 0.5 + 0.5 * Math.sin(a.t * 7);
            ctx.beginPath();
            ctx.arc(cx + p.x, cy - p.y, 4 + pulse * 3, 0, Math.PI * 2);
            ctx.fillStyle   = col;
            ctx.shadowColor = col;
            ctx.shadowBlur  = 24;
            ctx.fill();
            ctx.shadowBlur = 0;
          }
        }
      }

      ctx.restore();

      // Rim highlight
      const rim = ctx.createRadialGradient(cx, cy, r * 0.79, cx, cy, r);
      rim.addColorStop(0,    "rgba(60,100,255,0)");
      rim.addColorStop(0.78, "rgba(60,100,255,0.04)");
      rim.addColorStop(1,    "rgba(7,78,220,0.44)");
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = rim;
      ctx.fill();

      // City dots
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.clip();

      for (const key of CITY_KEYS) {
        const city = CITIES[key];
        const p = project(city.lon, city.lat, a.cLon, a.cLat, r);
        if (!p) continue;
        const px = cx + p.x, py = cy - p.y;
        const hi    = chData.active.includes(key);
        const pulse = reduced ? 0.5 : 0.5 + 0.5 * Math.sin(a.t * 2.4 + city.lon * 0.05);

        if (hi) {
          ctx.beginPath();
          ctx.arc(px, py, 12 + pulse * 7, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${0.03 + pulse * 0.04})`;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(px, py, 5.5, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(255,255,255,0.50)";
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(px, py, 3, 0, Math.PI * 2);
          ctx.fillStyle = "#ffffff";
          if (!reduced) { ctx.shadowColor = chData.color; ctx.shadowBlur = 18; }
          ctx.fill();
          ctx.shadowBlur = 0;
        } else {
          ctx.beginPath();
          ctx.arc(px, py, 1.8, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(130,165,255,${0.4 + p.c * 0.45})`;
          ctx.fill();
        }
      }
      ctx.restore();

      // City labels for active pair
      const labelKeys: CityKey[] = [];
      if (chData.from) labelKeys.push(chData.from);
      if (chData.to && chData.to !== chData.from) labelKeys.push(chData.to);

      ctx.font = "bold 10px system-ui, sans-serif";
      for (const key of labelKeys) {
        const city = CITIES[key];
        const p = project(city.lon, city.lat, a.cLon, a.cLat, r);
        if (!p) continue;
        const px = cx + p.x, py = cy - p.y;
        ctx.textAlign   = p.x > 0 ? "left" : "right";
        ctx.fillStyle   = "rgba(255,255,255,0.90)";
        ctx.shadowColor = "rgba(0,0,0,0.95)";
        ctx.shadowBlur  = 6;
        ctx.fillText(city.label, px + (p.x > 0 ? 12 : -12), py + 4);
        ctx.shadowBlur = 0;
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const chData = CHAPTERS[Math.min(chapter, CHAPTERS.length - 1)];

  return (
    // 500vh container: 6 chapters
    <div ref={containerRef} style={{ height: "500vh" }} className="relative bg-slate-950">

      {/* ── Sticky globe background (always behind panels) ─────────── */}
      <div
        className="sticky top-0 h-screen overflow-hidden"
        style={{ zIndex: 1 }}
      >
        <canvas
          ref={canvasRef}
          className="absolute inset-0 block w-full h-full"
        />

        {/* Desktop left gradient for text legibility */}
        <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-[50%] bg-gradient-to-r from-slate-950/92 via-slate-950/55 to-transparent md:block" />

        {/* Mobile bottom gradient */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[52%] bg-gradient-to-t from-slate-950/95 to-transparent md:hidden" />

        {/* Chapter progress dots — always visible on the sticky globe */}
        <div className="absolute right-5 top-1/2 z-20 hidden -translate-y-1/2 flex-col gap-2.5 md:flex">
          {CHAPTERS.map((ch, i) => (
            <div
              key={i}
              className="h-[3px] rounded-full transition-all duration-500"
              style={{
                width: i === chapter ? "1.8rem" : "0.4rem",
                backgroundColor:
                  i === chapter ? chData.color : "rgba(255,255,255,0.18)",
              }}
            />
          ))}
        </div>

        {/* Scroll hint — fades out after chapter 0 */}
        <div
          className="absolute bottom-8 left-[65%] flex -translate-x-1/2 flex-col items-center gap-1.5 text-white/35 transition-opacity duration-500"
          style={{ opacity: chapter === 0 ? 1 : 0, pointerEvents: "none" }}
        >
          <div className="h-7 w-px bg-gradient-to-b from-white/28 to-transparent" />
          <span className="text-[9px] uppercase tracking-[0.22em]">Scroll</span>
        </div>
      </div>

      {/* ── Scrollable chapter panels (pulled up to overlay the globe) ── */}
      <div
        style={{ marginTop: "-100vh", position: "relative", zIndex: 10 }}
      >
        {CHAPTERS.map((ch, i) => (
          <div
            key={i}
            className="flex h-screen flex-col justify-end pb-14 md:justify-center md:pb-0"
            style={{ pointerEvents: "none" }}
          >
            {/* Content card — left side on desktop, bottom on mobile */}
            <div
              className="px-6 md:px-14 w-full md:w-[46%]"
              style={{ pointerEvents: "auto" }}
            >
              <motion.div
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.55, once: false }}
                transition={{ duration: 0.50, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Glass panel backing */}
                <div className="rounded-2xl border border-white/10 bg-slate-950/65 px-7 py-7 backdrop-blur-md">
                  {/* Chapter number watermark */}
                  <div
                    className="mb-1 select-none text-[3.5rem] font-black leading-none md:text-[5rem]"
                    style={{ color: "rgba(255,255,255,0.06)" }}
                  >
                    {ch.num}
                  </div>

                  {/* Page label */}
                  <p
                    className="mb-2.5 text-[0.68rem] font-bold uppercase tracking-[0.18em]"
                    style={{ color: ch.color }}
                  >
                    {ch.label}
                  </p>

                  {/* Title */}
                  <h2 className="max-w-sm text-lg font-bold leading-snug text-white md:text-xl lg:text-2xl">
                    {ch.title}
                  </h2>

                  {/* Body */}
                  <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/60">
                    {ch.body}
                  </p>

                  {/* In-card dot indicator */}
                  <div className="mt-5 flex items-center gap-2">
                    {CHAPTERS.map((_, j) => (
                      <div
                        key={j}
                        className="h-[2px] rounded-full"
                        style={{
                          width: j === i ? "1.5rem" : "0.3rem",
                          backgroundColor:
                            j === i ? ch.color : "rgba(255,255,255,0.18)",
                        }}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
